import { ROADMAP_ITEMS } from './roadmap-data';
import { useEffect, useRef, useState, useCallback } from 'react';
import { bookingUrl } from '@/lib/booking';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import {
  IconLock, IconMessages, IconCoin, IconWorld, IconUserShield, IconKey, IconLink,
  IconActivityHeartbeat, IconHistory, IconCheck, IconSparkles, IconPaperclip, IconAt,
  IconUsers, IconClockCheck, IconApi,
} from '@tabler/icons-react';
import { IS_PRERENDER } from '@/lib/hydration';
import { ArtPanel } from '@/components/marketing/art-panel';
import { HeroTour } from '@/components/marketing/hero-tour';
import { FaqAccordion } from '@/components/marketing/faq-accordion';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { useReveal, Section, Eyebrow, H2, IconTile, Muted, Btn } from '@/components/marketing/warm/primitives';
import { SeatsVsTicketsCalculator } from '@/components/marketing/warm/seats-vs-tickets';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
} from '@/lib/seo';

// ── 1. Hero ──────────────────────────────────────────────────────
function HeroSection() {
  const ref = useReveal<HTMLDivElement>();
  const panelRef = useReveal<HTMLDivElement>(250);
  return (
    <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden warm-page">
      <div id="hero-sentinel" className="absolute top-0 left-0 w-1 h-1 pointer-events-none" />
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5" ref={ref}>
            <div className="hero-enter">
              <Link to="#portal-card" className="warm-eyebrow mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                NEW · Customer portal is live →
              </Link>

              <h1 className="font-display font-light text-[3rem] md:text-[3.75rem] xl:text-[4.1rem] leading-[1.05] tracking-tight mb-6" style={{ color: 'var(--warm-text)' }}>
                Pay for tickets,{' '}
                <span className="italic" style={{ color: 'var(--warm-muted)' }}>not seats.</span>
              </h1>

              <p className="text-lg xl:text-xl leading-relaxed mb-8" style={{ color: 'var(--warm-muted)' }}>
                Kora is the AI helpdesk and issue tracker that costs $0.10 per ticket. No seats, no monthly plans, unlimited members.
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
                <a href="#seats-section" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm transition-colors" style={{ color: 'var(--warm-muted)' }}>
                  See what you'd save →
                </a>
              </div>

              <p className="text-sm" style={{ color: 'var(--warm-muted)' }}>
                1,100 free credits · No credit card · EU-hosted
              </p>
            </div>
          </div>

          <div ref={panelRef} className="home-reveal lg:col-span-7 flex justify-center lg:justify-end">
            <HeroTour />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── 2. Proof strip ───────────────────────────────────────────────
function ProofStrip() {
  const ref = useReveal<HTMLUListElement>();
  const facts = [
    { icon: IconCoin, label: '$0.10 per ticket' },
    { icon: IconUsers, label: 'Unlimited members' },
    { icon: IconClockCheck, label: 'SLA, automation and webhooks free' },
    { icon: IconApi, label: '20+ API endpoints' },
    { icon: IconWorld, label: 'EU data hosting' },
  ];
  return (
    <div style={{ borderTop: '1px solid var(--warm-border)', borderBottom: '1px solid var(--warm-border)', background: 'var(--warm-card)' }}>
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-6">
        <ul ref={ref} className="reveal-stagger flex flex-wrap justify-center gap-2 sm:gap-2.5">
          {facts.map(({ icon: Icon, label }) => (
            <li key={label} className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full pl-1 sm:pl-1.5 pr-3 sm:pr-4 py-1 sm:py-1.5 text-[13px] sm:text-sm font-medium"
              style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>
              <span className="inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full shrink-0" style={{ background: 'var(--warm-card)', color: 'var(--warm-text)' }}>
                <Icon size={14} stroke={1.75} />
              </span>
              {label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── 3. Live in minutes ───────────────────────────────────────────
function LiveInMinutes() {
  const ref = useReveal<HTMLDivElement>();
  const steps = [
    { title: 'Sign in', body: 'Use Google or a one-time email link. No password to create.', mock: (
      <Link to="/auth?start=google" className="setup-cta whitespace-nowrap flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border-strong)', color: 'var(--warm-text)' }}>
        <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        Continue with Google
      </Link>
    )},
    { title: 'Set up your workspace', body: 'Name it, choose how work is assigned, and pick your ticket categories.', mock: (
      <div className="px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
        <span className="text-gray-500 text-xs">Workspace name</span>
        <p className="font-medium text-gray-800">Northwind Support</p>
      </div>
    )},
    { title: 'Claim 1,100 free credits', body: 'Two clicks. No credit card.', mock: (
      <Link to="/auth?start=google" className="setup-cta whitespace-nowrap flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium" style={{ background: 'var(--warm-btn)', color: 'var(--warm-btn-text)' }}>
        Claim 1,100 credits <ArrowRight className="size-3.5" />
      </Link>
    )},
    { title: 'Paste your first ticket', body: 'Drop in any message and Kora structures it into a ticket.', mock: (
      <div className="px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
        <span className="font-semibold text-gray-800">Mobile checkout failing</span>
        <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] bg-red-50 text-red-700">High</span>
      </div>
    )},
  ];

  return (
    <Section>
      <div ref={ref} className="home-reveal">
        <Eyebrow>Setup</Eyebrow>
        <H2>Live in minutes, <br /><Muted>not days.</Muted></H2>
        <p className="mt-4 text-lg max-w-xl leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
          No card, no sales call, no setup project. Sign in and your helpdesk is running before your coffee cools.
        </p>

        <div className="reveal-stagger mt-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="warm-card p-5 flex flex-col" style={{ border: '1px solid var(--warm-border)' }}>
              <div className="text-3xl font-display font-light mb-3" style={{ color: 'var(--warm-muted)' }}>{String(i + 1).padStart(2, '0')}</div>
              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--warm-text)' }}>{s.title}</p>
              <p className="text-sm mb-5" style={{ color: 'var(--warm-muted)' }}>{s.body}</p>
              <div className="mt-auto">{s.mock}</div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-sm leading-relaxed max-w-2xl" style={{ color: 'var(--warm-muted)' }}>
          Freshdesk users report setups from about a day to six weeks, most from a few days to three weeks.¹ Small Zendesk rollouts are typically quoted at 2 to 4 weeks.² Kora takes minutes.
        </p>
        <p className="mt-2 text-xs" style={{ color: 'var(--warm-muted)' }}>
          ¹ <a href="https://www.peerspot.com/products/freshdesk-initial-setup" rel="nofollow noopener" target="_blank" className="underline">PeerSpot, "Freshdesk Initial Setup" user reviews</a> &nbsp;
          ² <a href="https://www.eesel.ai/blog/zendesk-implementations" rel="nofollow noopener" target="_blank" className="underline">eesel AI, "A guide to Zendesk implementations in 2026"</a>
        </p>
      </div>
    </Section>
  );
}

// ── 4. Seats vs tickets calculator ──────────────────────────────
function SeatsVsTickets() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <Section id="seats-section" className="warm-page" style={{ borderTop: '1px solid var(--warm-border)', borderBottom: '1px solid var(--warm-border)' } as React.CSSProperties}>
      <div ref={ref} className="home-reveal">
        <Eyebrow>Pricing math</Eyebrow>
        <H2>Seats add up. <br /><Muted>Tickets don't.</Muted></H2>
        <p className="mt-4 text-lg max-w-xl leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
          Per-seat helpdesks charge for every agent, every month, whether they're busy or not. Kora charges $0.10 for each ticket created and nothing per seat. Put in your own numbers.
        </p>

        <div className="mt-10">
          <SeatsVsTicketsCalculator />
        </div>
      </div>
    </Section>
  );
}

// ── 5–8. Stacking capability cards ──────────────────────────────
function CapabilityCard({ id, eyebrow, title, body, points, footnote, tint, demo, reverse = false, learnMore }: {
  id?: string; learnMore?: { label: string; to: string }; eyebrow: string; title: string; body: string; points: string[]; footnote?: string; tint: Parameters<typeof ArtPanel>[0]['tint']; demo: React.ReactNode; reverse?: boolean;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div id={id} className="stack-card py-16 md:py-20">
      <div ref={ref} className="stack-inner home-reveal max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className={`grid lg:grid-cols-2 gap-12 items-center ${reverse ? 'lg:grid-flow-col-dense' : ''}`}>
          <div className={reverse ? 'lg:col-start-2' : ''}>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h3 className="font-display font-light text-[1.75rem] md:text-[2.25rem] leading-tight tracking-tight mb-4" style={{ color: 'var(--warm-text)' }}>{title}</h3>
            <p className="text-lg leading-relaxed mb-6" style={{ color: 'var(--warm-muted)' }}>{body}</p>
            <ul className="space-y-2.5 mb-6">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--warm-muted)' }}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--warm-muted)' }} />
                  {p}
                </li>
              ))}
            </ul>
            {footnote && <p className="text-xs mb-6" style={{ color: 'var(--warm-muted)' }}>{footnote}</p>}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
              {learnMore && <Link to={learnMore.to} className="text-sm font-medium underline" style={{ color: 'var(--warm-text)' }}>{learnMore.label}</Link>}
            </div>
          </div>
          <div className={reverse ? 'lg:col-start-1 lg:row-start-1' : ''}>
            <ArtPanel tint={tint} className="p-6 md:p-8 min-h-[280px] flex items-center justify-center">
              {demo}
            </ArtPanel>
          </div>
        </div>
      </div>
    </div>
  );
}

function AIStructuringDemo() {
  const [source, setSource] = useState(0);
  const sources = [
    { label: 'Slack message', title: 'CSV export times out for large accounts', category: 'Reports', priority: 'Medium', color: 'var(--sev-blue)' },
    { label: 'Email', title: 'Duplicate charge on October invoice', category: 'Billing', priority: 'High', color: 'var(--sev-red)' },
    { label: 'Note', title: 'New hire cannot access VPN before start date', category: 'Access', priority: 'High', color: 'var(--sev-red)' },
  ];
  const s = sources[source];
  return (
    <div className="w-full space-y-4">
      <div className="flex gap-2 flex-wrap">
        {sources.map((src, i) => (
          <button key={src.label} onClick={() => setSource(i)} className="text-xs px-3 py-1.5 rounded-full font-medium transition-all" style={{ background: i === source ? 'var(--warm-inv)' : 'var(--warm-card)', color: i === source ? 'var(--warm-inv-text)' : 'var(--warm-muted)', border: '1px solid var(--warm-border)' }}>
            {src.label}
          </button>
        ))}
      </div>
      <div className="rounded-xl p-4" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
        <p className="text-xs font-semibold text-gray-500 mb-2">Structured ticket</p>
        <p className="font-semibold text-gray-900 text-sm mb-2">{s.title}</p>
        <div className="flex gap-2 flex-wrap">
          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{s.category}</span>
          <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: `color-mix(in srgb, ${s.color} 12%, transparent)`, color: s.color }}>{s.priority}</span>
        </div>
      </div>
    </div>
  );
}

function SLADemo() {
  const [tick, setTick] = useState(0);
  useEffect(() => { if (IS_PRERENDER) return; const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const mins = 42 - Math.floor(tick / 2) % 43;
  const pct = mins / 60;
  const color = pct > 0.5 ? '#22c55e' : pct > 0.2 ? '#f59e0b' : '#ef4444';
  return (
    <div className="w-full space-y-4">
      <div className="rounded-xl p-4" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
        <div className="flex items-center gap-4">
          <svg width="48" height="48" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="20" fill="none" stroke="var(--warm-border)" strokeWidth="4" />
            <circle cx="24" cy="24" r="20" fill="none" stroke={color} strokeWidth="4" strokeDasharray={`${2 * Math.PI * 20 * pct} ${2 * Math.PI * 20 * (1 - pct)}`} strokeLinecap="round" transform="rotate(-90 24 24)" style={{ transition: 'stroke-dasharray 1s ease' }} />
          </svg>
          <div>
            <p className="text-xs text-gray-500">First response</p>
            <p className="font-semibold text-gray-900">{mins > 0 ? `${mins} min left` : 'Paused · Waiting for User'}</p>
          </div>
        </div>
      </div>
      <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
        <div className="text-xs font-semibold text-gray-500 mb-2">Automation rule</div>
        {[['When', 'a ticket is created'], ['If', 'category is Payments'], ['Then', 'priority → High, assign to Sarah Johnson']].map(([k, v]) => (
          <div key={k} className="flex gap-2 text-xs"><span className="font-semibold text-gray-500 w-8">{k}</span><span className="text-gray-700">{v}</span></div>
        ))}
        <div className="mt-2 text-xs px-2 py-1 rounded-full inline-block" style={{ background: 'var(--chip-green-bg)', color: 'var(--chip-green)' }}>Dry run · 3 tickets would match</div>
      </div>
    </div>
  );
}

function PortalDemo() {
  const [swatch, setSwatch] = useState(0);
  const swatches = [
    { label: 'Graphite', accent: '#3A3630' },
    { label: 'Blue', accent: '#2563eb' },
    { label: 'Rose', accent: '#e11d48' },
  ];
  const acc = swatches[swatch].accent;
  return (
    <div className="w-full space-y-3">
      <div className="rounded-xl overflow-hidden shadow-sm" style={{ border: '1px solid var(--warm-border)' }}>
        <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: acc, color: '#fff' }}>
          <span className="font-semibold text-sm">Northwind Support</span>
          <span className="text-xs opacity-80">+ New request</span>
        </div>
        <div className="p-4 text-xs space-y-2" style={{ background: 'var(--warm-inset)' }}>
          <div className="text-gray-500 text-[11px] mb-1">Open · 2 / Closed · 5</div>
          {[['Cannot download my invoice', 'Waiting on you'], ['Update billing address', 'In progress'], ['Add a teammate', 'Resolved']].map(([t, s]) => (
            <div key={t} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid var(--warm-border)' }}>
              <span className="text-gray-800">{t}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: s === 'Waiting on you' ? 'var(--chip-amber-bg)' : s === 'In progress' ? 'var(--chip-blue-bg)' : 'var(--chip-green-bg)', color: s === 'Waiting on you' ? 'var(--chip-amber)' : s === 'In progress' ? 'var(--chip-blue)' : 'var(--chip-green)' }}>{s}</span>
            </div>
          ))}
          <p className="text-[11px] text-gray-500 pt-1">usekora.app/p/portal/your-company</p>
        </div>
      </div>
      <div className="flex gap-2">
        {swatches.map((sw, i) => (
          <button key={sw.label} onClick={() => setSwatch(i)} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full transition-all" style={{ border: `1px solid ${i === swatch ? (sw.label === 'Graphite' ? 'var(--warm-text)' : sw.accent) : 'var(--warm-border)'}`, color: i === swatch ? (sw.label === 'Graphite' ? 'var(--warm-text)' : sw.accent) : 'var(--warm-muted)', fontWeight: i === swatch ? 600 : 400 }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: sw.accent }} />{sw.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function HelpCenterDemo() {
  const [phase, setPhase] = useState<'progress' | 'drafts' | 'search'>('progress');
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (IS_PRERENDER) return;
    const t1 = setTimeout(() => setPhase('drafts'), 2500);
    const t2 = setTimeout(() => setPhase('search'), 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  const drafts = ['How do I reset my password?', 'Updating your billing address', 'Downloading invoices', 'Adding a teammate'];
  const filtered = phase === 'search' ? drafts.filter((d) => d.toLowerCase().includes(query.toLowerCase() || 'reset')) : drafts;
  return (
    <div className="w-full space-y-3">
      {phase === 'progress' && (
        <div className="rounded-xl p-4" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
          <p className="text-xs font-semibold text-gray-500 mb-3">Magic Helpdesk</p>
          {['Reading northwindsupport.com · 9 pages', 'Planning articles…', 'Drafting 6 of 8'].map((s, i) => (
            <div key={s} className="flex items-center gap-2 mb-1.5 text-xs text-gray-600">
              <div className="w-3 h-3 rounded-full border border-gray-300 border-t-transparent animate-spin" style={{ animationDelay: `${i * 200}ms` }} />
              {s}
            </div>
          ))}
        </div>
      )}
      {phase !== 'progress' && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--warm-border)' }}>
          {phase === 'search' && (
            <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--warm-border)', background: 'var(--warm-inset)' }}>
              <input className="w-full text-sm outline-none bg-transparent" placeholder="Search help articles…" value={query || 'reset password'} readOnly />
            </div>
          )}
          <div className="divide-y" style={{ borderColor: 'var(--warm-border)' }}>
            {filtered.map((d) => (
              <div key={d} className="px-4 py-2.5 text-sm text-gray-800" style={{ background: 'var(--warm-surface)' }}>
                {d}
                {d.includes('reset') && phase === 'search' && <span className="ml-2 text-xs text-gray-500">1,240 views · 92% helpful</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function useStackingCards(container: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const root = container.current;
    if (!root || IS_PRERENDER) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const update = () => {
      frame = 0;
      const cards = [...root.querySelectorAll<HTMLElement>('.stack-card')];
      cards.forEach((card, i) => {
        const next = cards[i + 1];
        if (!next || !desktop.matches || reduced.matches) {
          card.style.transform = '';
          card.style.removeProperty('--stack-p');
          return;
        }
        const covered = card.offsetHeight - (next.getBoundingClientRect().top - card.getBoundingClientRect().top);
        const p = Math.min(1, Math.max(0, covered / card.offsetHeight));
        card.style.transform = `scale(${1 - p * 0.06})`;
        card.style.setProperty('--stack-p', p.toFixed(3));
      });
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [container]);
}

function CapabilitySection() {
  const ref = useReveal<HTMLDivElement>();
  const stackRef = useRef<HTMLElement>(null);
  useStackingCards(stackRef);
  return (
    <section id="product" ref={stackRef} style={{ borderTop: '1px solid var(--warm-border)' }}>
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16">
        <div ref={ref} className="home-reveal">
          <Eyebrow>What Kora does</Eyebrow>
          <H2>One helpdesk. <br /><Muted>Every ticket, handled.</Muted></H2>
        </div>
      </div>

      <CapabilityCard
        eyebrow="AI structuring"
        title="Paste the mess. Get a ticket."
        body="Slack threads, customer emails, quick notes — paste anything and Kora's AI turns it into a clean ticket."
        points={['Writes a clear title and description', 'Picks the right category', 'Sets the priority']}
        footnote="Free, with no add-on fee."
        tint="ai"
        demo={<AIStructuringDemo />}
      />
      <CapabilityCard
        eyebrow="SLAs & automation"
        title="SLAs and automation, without a price tier."
        body="Set response and resolution targets, and let rules do the routine work. Both are free on every workspace."
        points={['Business-hours clocks that pause while you wait on the customer', 'Breach alerts before anyone has to ask', 'If-this-then-that rules with a dry run to test them safely']}
        tint="sla"
        demo={<SLADemo />}
        reverse
      />
      <CapabilityCard
        id="portal-card"
        eyebrow="Customer portal"
        title="Give your customers their own portal."
        body="Customers sign in with an emailed link — no password, no account — and see only their own requests."
        points={['Reply, reopen and raise new requests', 'Download or delete their own data', 'Your logo and colours']}
        footnote="Off by default. Browsing and replies are free; a new request is 1 credit."
        tint="portal"
        learnMore={{ label: 'Customer portal software', to: '/customer-portal-software' }}
        demo={<PortalDemo />}
      />
      <CapabilityCard
        eyebrow="Help center"
        title="Answer questions before they become tickets."
        body="Publish a branded help center your customers can search, with article analytics to show what's working."
        points={['Blog-style articles with search', '"Was this helpful?" votes', 'Views and search analytics']}
        tint="help"
        demo={<HelpCenterDemo />}
        reverse
      />
    </section>
  );
}

// ── 9. AI Analyst ────────────────────────────────────────────────
const ANALYST_TABS = [
  {
    q: 'How many open tickets are there?',
    answer: 'There are 37 open tickets right now: 21 Open, 9 In Progress and 7 Waiting for User.',
    rows: [['Open', '21'], ['In Progress', '9'], ['Waiting for User', '7']],
    sql: `SELECT status, COUNT(*) AS tickets FROM tickets WHERE status IN ('Open','In Progress','Waiting for User') GROUP BY status`,
    followup: { q: 'And how many of those are High priority?', a: '9 of the 37 open tickets are High priority.' },
  },
  {
    q: 'What are the most common categories?',
    answer: 'Billing leads this month with 64 tickets, followed by Access (41) and Payments (38).',
    rows: [['Billing', '64'], ['Access', '41'], ['Payments', '38'], ['Reports', '27'], ['Other', '12']],
    sql: `SELECT category, COUNT(*) AS tickets FROM tickets GROUP BY category ORDER BY tickets DESC LIMIT 5`,
  },
  {
    q: 'Show me tickets assigned to each team member',
    answer: 'Emily Carter has the most open tickets (18), then James Miller (15) and Sarah Johnson (12). 6 tickets are unassigned.',
    rows: [['Emily Carter', '18'], ['James Miller', '15'], ['Sarah Johnson', '12'], ['Unassigned', '6']],
    sql: `SELECT COALESCE(assignee_name,'Unassigned') AS assignee, COUNT(*) AS open_tickets FROM tickets WHERE status != 'Closed' GROUP BY assignee ORDER BY open_tickets DESC`,
  },
  {
    q: 'Which High priority tickets are still open?',
    answer: '5 High priority tickets are still open. The oldest is 3 days old.',
    rows: [['#1043', 'CSV export times out for large accounts', '3d'], ['#1047', 'Duplicate charge on October invoice', '2d'], ['#1049', 'New hire cannot access VPN', '1d']],
    sql: `SELECT ticket_number, title, created_at FROM tickets WHERE priority = 'High' AND status NOT IN ('Closed','Resolved') ORDER BY created_at ASC`,
  },
];

function AIAnalystSection() {
  const ref = useReveal<HTMLDivElement>();
  const [activeTab, setActiveTab] = useState(0);
  const [showSql, setShowSql] = useState(false);
  const tab = ANALYST_TABS[activeTab];

  return (
    <Section style={{ background: 'var(--warm-card)', borderTop: '1px solid var(--warm-border)', borderBottom: '1px solid var(--warm-border)' } as React.CSSProperties}>
      <div ref={ref} className="home-reveal">
        <Eyebrow>AI Analyst</Eyebrow>
        <H2>Ask your tickets <br /><Muted>anything.</Muted></H2>
        <p className="mt-4 text-lg max-w-xl leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
          Plain-English questions, answered from your own ticket data in seconds.
        </p>

        <div className="mt-12 grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            <ul className="space-y-3">
              {[
                { icon: IconLock, title: 'Read-only and private', body: 'Every query is a safe, read-only lookup scoped to your workspace.' },
                { icon: IconMessages, title: 'Remembers the conversation', body: 'Ask a follow-up and it knows what "those" means.' },
                { icon: IconCoin, title: '1 credit per answer', body: 'Failed answers are free. For owners, admins and managers.' },
              ].map((p) => (
                <li key={p.title} className="flex gap-3.5">
                  <IconTile icon={p.icon} />
                  <div>
                    <p className="font-semibold text-sm mb-0.5" style={{ color: 'var(--warm-text)' }}>{p.title}</p>
                    <p className="text-sm" style={{ color: 'var(--warm-muted)' }}>{p.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
          </div>

          <div className="warm-card rounded-2xl overflow-hidden" style={{ border: '1px solid var(--warm-border)' }}>
            <div className="flex overflow-x-auto" style={{ borderBottom: '1px solid var(--warm-border)' }}>
              {ANALYST_TABS.map((t, i) => (
                <button key={i} onClick={() => { setActiveTab(i); setShowSql(false); }}
                  className="px-4 py-3 text-xs font-medium whitespace-nowrap transition-colors shrink-0"
                  style={{ color: i === activeTab ? 'var(--warm-text)' : 'var(--warm-muted)', borderBottom: i === activeTab ? '2px solid var(--warm-text)' : '2px solid transparent', background: 'none', border: i === activeTab ? '0 0 2px 0 solid var(--warm-text)' : 'none' }}>
                  {t.q}
                </button>
              ))}
            </div>
            <div className="p-5 tab-panel" key={activeTab}>
              <p className="text-sm mb-4" style={{ color: 'var(--warm-muted)' }}>{tab.answer}</p>
              <table className="w-full text-xs rounded-lg overflow-hidden">
                <tbody>
                  {tab.rows.map((row) => (
                    <tr key={row[0]} style={{ borderBottom: '1px solid var(--warm-border)' }}>
                      {row.map((cell, ci) => (
                        <td key={ci} className="py-2 pr-4" style={{ color: ci === 0 ? 'var(--warm-text)' : 'var(--warm-muted)', fontWeight: ci === 0 ? 500 : 400 }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex items-center justify-between text-xs" style={{ color: 'var(--warm-muted)' }}>
                <span>Based on data from October 2026</span>
                <button onClick={() => setShowSql((s) => !s)} className="underline">{showSql ? 'Hide SQL' : 'Show SQL'}</button>
              </div>
              {showSql && (
                <pre className="mt-3 p-3 rounded-lg text-xs overflow-x-auto" style={{ background: '#1C1A15', color: '#a8b4b0' }}>
                  {tab.sql}
                </pre>
              )}
              {'followup' in tab && tab.followup && (
                <div className="mt-3 p-3 rounded-lg text-xs" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)' }}>
                  <p className="font-medium mb-1" style={{ color: 'var(--warm-text)' }}>↳ {tab.followup.q}</p>
                  <p style={{ color: 'var(--warm-muted)' }}>{tab.followup.a}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ── 10. Team ─────────────────────────────────────────────────────
const MINI_CARD: React.CSSProperties = { background: 'var(--warm-surface)', border: '1px solid var(--warm-border)', boxShadow: '0 8px 24px -16px rgba(28,26,21,0.25)' };

function Avatar({ initials, bg }: { initials: string; bg: string }) {
  return <span className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-semibold text-white shrink-0" style={{ background: bg }}>{initials}</span>;
}

function TeamConversationVisual() {
  return (
    <div className="w-full max-w-[240px] space-y-2">
      <div className="rounded-xl p-2.5 flex gap-2" style={MINI_CARD}>
        <Avatar initials="EC" bg="#7C6F5B" />
        <p className="text-[11px] leading-snug text-gray-700">
          <span className="inline-flex items-center px-1 rounded" style={{ background: 'var(--chip-blue-bg)', color: 'var(--chip-blue)' }}><IconAt size={10} stroke={2} />James</span> can you check the refund?
        </p>
      </div>
      <div className="rounded-xl p-2.5 flex gap-2 ml-6" style={MINI_CARD}>
        <Avatar initials="JM" bg="#3A3630" />
        <div className="text-[11px] leading-snug text-gray-700">
          On it. Receipt attached.
          <span className="mt-1.5 flex items-center gap-1 text-[10px] text-gray-500"><IconPaperclip size={11} stroke={1.75} />receipt-1047.pdf</span>
        </div>
      </div>
    </div>
  );
}

function TeamRolesVisual() {
  const roles = ['Owner', 'Admin', 'Manager', 'Operations', 'Agent', 'Viewer'];
  return (
    <div className="w-full max-w-[240px] rounded-xl p-3" style={MINI_CARD}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Invite as</p>
      <div className="flex flex-wrap gap-1.5">
        {roles.map((r) => (
          <span key={r} className="text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
            style={r === 'Agent' ? { background: 'var(--warm-inv)', color: 'var(--warm-inv-text)' } : { background: 'var(--warm-card)', color: 'var(--warm-muted)' }}>
            {r === 'Agent' && <IconCheck size={10} stroke={2.5} />}{r}
          </span>
        ))}
      </div>
    </div>
  );
}

function TeamDashboardVisual() {
  const bars = [38, 62, 48, 80, 56, 72, 90];
  return (
    <div className="w-full max-w-[240px] rounded-xl p-3" style={MINI_CARD}>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Resolved · 7 days</p>
        <p className="text-sm font-semibold text-gray-900">142</p>
      </div>
      <div className="flex items-end gap-1.5 h-16">
        {bars.map((h, i) => (
          <span key={i} className="team-bar flex-1 rounded-sm" style={{ height: `${h}%`, background: i === bars.length - 1 ? 'var(--warm-text)' : 'var(--warm-handle)', transitionDelay: `${300 + i * 60}ms` }} />
        ))}
      </div>
    </div>
  );
}

function TeamSection() {
  const ref = useReveal<HTMLDivElement>();
  const cards: { title: string; body: string; tint: Parameters<typeof ArtPanel>[0]['tint']; visual: React.ReactNode }[] = [
    { title: 'Conversations', body: 'Comment, mention a teammate, attach files. Every ticket keeps its full history.', tint: 'ai', visual: <TeamConversationVisual /> },
    { title: 'Roles', body: 'Owner, Admin, Manager, Operations, Agent and Viewer. Permissions are checked on every request.', tint: 'portal', visual: <TeamRolesVisual /> },
    { title: 'Dashboards', body: 'Live counts of created, open and resolved tickets, resolution time and category trends.', tint: 'analyst', visual: <TeamDashboardVisual /> },
  ];
  return (
    <Section>
      <div ref={ref} className="home-reveal">
        <Eyebrow>Your team</Eyebrow>
        <H2>Everyone on <br /><Muted>the same ticket.</Muted></H2>
        <p className="mt-3 text-sm" style={{ color: 'var(--warm-muted)' }}>Members are always free. Invite the whole team.</p>
        <div className="reveal-stagger mt-10 grid sm:grid-cols-3 gap-5">
          {cards.map((c) => (
            <div key={c.title} className="warm-card p-5" style={{ border: '1px solid var(--warm-border)' }}>
              <ArtPanel tint={c.tint} className="h-44 mb-5 flex items-center justify-center p-4">
                {c.visual}
              </ArtPanel>
              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--warm-text)' }}>{c.title}</p>
              <p className="text-sm" style={{ color: 'var(--warm-muted)' }}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ── 11. Developers ───────────────────────────────────────────────
const DEV_TABS = [
  {
    label: 'Create ticket',
    code: `POST /public/v1/tickets\n\n{\n  "title": "Checkout page crashing on card payments",\n  "description": "Card payments fail at the last step since this morning.",\n  "category": "Payments",\n  "priority": "High",\n  "reporter_email": "customer@example.com"\n}`,
  },
  {
    label: 'Webhook payload',
    code: `// ticket.created  X-Kora-Signature: t=…,v1=…\n\n{\n  "event": "ticket.created",\n  "workspace_id": "ws_EXAMPLE",\n  "timestamp": "2026-10-18T09:12:44.000Z",\n  "data": {\n    "ticket_number": 1051,\n    "title": "Mobile checkout failing",\n    "category": "Payments",\n    "priority": "High",\n    "status": "Open"\n  }\n}`,
  },
  {
    label: 'Embed session',
    code: `POST /public/v1/sessions\n\n{ "user_email": "agent@yourcompany.com",\n  "duration_minutes": 480,\n  "theme": "light" }`,
  },
];

function DeveloperSection() {
  const ref = useReveal<HTMLDivElement>();
  const [tab, setTab] = useState(0);
  return (
    <Section style={{ background: 'var(--warm-inv)' } as React.CSSProperties}>
      <div ref={ref} className="home-reveal">
        <p className="warm-eyebrow mb-4" style={{ color: '#8a8479' }}>Developers</p>
        <h2 className="font-display font-light text-[2rem] md:text-[2.75rem] leading-tight tracking-tight mb-4" style={{ color: '#F2F0EA' }}>
          Build on Kora, <br /><span style={{ color: '#8a8479', fontStyle: 'italic' }}>not around it.</span>
        </h2>
        <p className="text-lg max-w-xl leading-relaxed mb-4" style={{ color: '#8a8479' }}>
          A clean REST API with 20+ endpoints, 10 webhook events signed with HMAC-SHA256, embedded sessions for your own product, and a 2 KB form widget for any website.
        </p>
        <div className="flex flex-wrap gap-3 mb-2">
          <Link to="/docs" className="text-sm inline-flex items-center gap-1" style={{ color: '#BFD3DF' }}>API reference <ExternalLink className="size-3" /></Link>
          <Link to="/docs#webhooks-overview" className="text-sm inline-flex items-center gap-1" style={{ color: '#BFD3DF' }}>Webhooks <ExternalLink className="size-3" /></Link>
          <Link to="/docs#embedded-overview" className="text-sm inline-flex items-center gap-1" style={{ color: '#BFD3DF' }}>Embedded sessions <ExternalLink className="size-3" /></Link>
        </div>
        <p className="text-xs mb-8" style={{ color: 'var(--warm-inv-muted)' }}>API calls cost 1 credit per 400 responses.</p>

        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #2A2720' }}>
          <div className="flex" style={{ borderBottom: '1px solid #2A2720' }}>
            {DEV_TABS.map((t, i) => (
              <button key={t.label} onClick={() => setTab(i)} className="px-4 py-3 text-xs font-medium transition-colors" style={{ color: i === tab ? '#F2F0EA' : 'var(--warm-inv-muted)', borderBottom: i === tab ? '2px solid #F2F0EA' : '2px solid transparent', background: 'none', cursor: 'pointer' }}>
                {t.label}
              </button>
            ))}
          </div>
          <pre className="p-5 text-xs leading-relaxed overflow-x-auto tab-panel" key={tab} style={{ color: '#a8b4b0', background: '#1C1A15', fontFamily: 'monospace' }}>
            {DEV_TABS[tab].code}
          </pre>
        </div>
      </div>
    </Section>
  );
}

// ── 12. Who it's for ─────────────────────────────────────────────
function WhoSection() {
  const ref = useReveal<HTMLDivElement>();
  const teams = [
    { name: 'Every team in the company', job: 'One workspace for support, IT, finance and product. Nobody counts as a seat.', points: ['Unlimited members', 'Six roles', 'Pay per ticket, not per head'], link: '/solutions/company-wide' },
    { name: 'Customer support teams', job: 'Answer customers fast without paying for every seat.', points: ['AI-structured tickets', 'SLAs with business hours', 'A customer portal'], link: '/solutions/customer-support' },
    { name: 'IT and internal helpdesks', job: 'Track every internal request in one place.', points: ['Paste any message, AI structures it', 'Automation rules', 'Six roles'], link: '/solutions/it-teams' },
    { name: 'Ops and engineering teams', job: 'Turn scattered issues into owned, tracked work.', points: ['Claim-based ownership', 'REST API and webhooks', 'AI Analyst'], link: '/solutions/product-engineering' },
  ];
  return (
    <Section style={{ borderTop: '1px solid var(--warm-border)' } as React.CSSProperties}>
      <div ref={ref} className="home-reveal">
        <Eyebrow>Who it's for</Eyebrow>
        <H2>Built for teams <br /><Muted>and the whole company.</Muted></H2>
        <div className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {teams.map((t) => (
            <div key={t.name} className="warm-card p-6 flex flex-col" style={{ border: '1px solid var(--warm-border)' }}>
              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--warm-text)' }}>{t.name}</p>
              <p className="text-sm mb-4" style={{ color: 'var(--warm-muted)' }}>{t.job}</p>
              <ul className="space-y-1.5 mb-6 flex-1">
                {t.points.map((p) => <li key={p} className="text-sm flex items-center gap-2" style={{ color: 'var(--warm-muted)' }}><span className="w-1 h-1 rounded-full" style={{ background: 'var(--warm-muted)', flexShrink: 0 }} />{p}</li>)}
              </ul>
              <Link to={t.link} className="text-sm font-medium inline-flex items-center gap-1" style={{ color: 'var(--warm-text)' }}>
                Read the guide <ArrowRight className="size-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ── 13. Compare ──────────────────────────────────────────────────
function CompareSection() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <Section style={{ background: 'var(--warm-card)', borderTop: '1px solid var(--warm-border)', borderBottom: '1px solid var(--warm-border)' } as React.CSSProperties}>
      <div ref={ref} className="home-reveal">
        <Eyebrow>Compare</Eyebrow>
        <H2>See how Kora <br /><Muted>stacks up.</Muted></H2>
        <div className="reveal-stagger mt-10 grid sm:grid-cols-3 gap-4">
          {[
            { label: 'Kora vs Freshdesk', href: '/compare/kora-vs-freshdesk' },
            { label: 'Kora vs Zendesk', href: '/compare/kora-vs-zendesk' },
            { label: 'All 9 comparisons', href: '/compare' },
          ].map((c) => (
            <Link key={c.href} to={c.href} className="warm-card p-5 flex items-center justify-between text-sm font-medium transition-transform hover:-translate-y-0.5" style={{ border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>
              {c.label} <ArrowRight className="size-4" style={{ color: 'var(--warm-muted)' }} />
            </Link>
          ))}
        </div>
        <p className="mt-5 text-sm" style={{ color: 'var(--warm-muted)' }}>
          Switching? Read the{' '}
          <Link to="/freshdesk-alternative" className="underline">Freshdesk alternative</Link> and{' '}
          <Link to="/zendesk-alternative" className="underline">Zendesk alternative</Link> guides.
        </p>
      </div>
    </Section>
  );
}

// ── 14. Pricing ──────────────────────────────────────────────────
function PricingSection() {
  const ref = useReveal<HTMLDivElement>();
  const included = ['SLA tracking with business hours', 'Automation rules with dry run', 'AI ticket structuring', 'Customer portal', 'Six roles and permissions', 'REST API and signed webhooks', 'Help center with analytics', 'Unlimited members'];
  return (
    <Section>
      <div ref={ref} className="home-reveal grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-6">
          <Eyebrow>Pricing</Eyebrow>
          <H2>Pay per ticket. <br /><Muted>Not per seat.</Muted></H2>

          <div className="mt-8 rounded-2xl p-6 relative overflow-hidden" style={{ background: 'var(--warm-inv)', color: 'var(--warm-inv-text)', border: '1px solid var(--warm-border)' }}>
            <div className="absolute -right-10 -top-16 w-56 h-56 rounded-full opacity-40 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, #E7B98A 0%, transparent 70%)' }} />
            <p className="relative flex items-center gap-2 text-xs font-medium uppercase tracking-wider" style={{ color: '#C9C2B4' }}>
              <IconSparkles size={14} stroke={1.75} /> Free to start · No card
            </p>
            <p className="relative mt-3 flex items-baseline gap-3">
              <span className="font-display font-light text-6xl leading-none">1,100</span>
              <span className="text-lg" style={{ color: '#C9C2B4' }}>free credits</span>
            </p>
            <p className="relative mt-3 text-sm leading-relaxed" style={{ color: '#C9C2B4' }}>
              That's 1,100 tickets on us: 1,000 credits to use in your first 30 days, plus 100 that stay valid for 10 years.
            </p>
          </div>

          <div className="mt-8">
            <p className="font-semibold text-sm" style={{ color: 'var(--warm-text)' }}>Every "enterprise" feature, included.</p>
            <p className="text-sm mt-1 mb-4" style={{ color: 'var(--warm-muted)' }}>Things other helpdesks keep for their top tiers cost nothing extra here.</p>
            <ul className="reveal-stagger grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {included.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm" style={{ color: 'var(--warm-muted)' }}>
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0" style={{ background: 'var(--chip-green-bg)', color: 'var(--chip-green)' }}><IconCheck size={12} stroke={2.5} /></span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-6 lg:pt-16">
          <div className="warm-card p-8" style={{ border: '1px solid var(--warm-border)', boxShadow: '0 30px 60px -40px rgba(28,26,21,0.35)' }}>
            <p className="text-sm font-medium mb-3" style={{ color: 'var(--warm-muted)' }}>Pay as you go</p>
            <p className="flex items-baseline gap-2">
              <span className="font-display font-light text-6xl leading-none" style={{ color: 'var(--warm-text)' }}>$0.10</span>
              <span className="text-sm" style={{ color: 'var(--warm-muted)' }}>per ticket</span>
            </p>
            <div className="my-6 h-px" style={{ background: 'var(--warm-border)' }} />
            <div className="space-y-3">
              {[['300 tickets', '$30'], ['1,000 tickets', '$100'], ['5,000 tickets', '$500']].map(([t, p]) => (
                <div key={t} className="flex items-center justify-between text-sm">
                  <span style={{ color: 'var(--warm-muted)' }}>{t} a month</span>
                  <span className="font-medium" style={{ color: 'var(--warm-text)' }}>{p}</span>
                </div>
              ))}
            </div>
            <div className="my-6 h-px" style={{ background: 'var(--warm-border)' }} />
            <ul className="space-y-1.5 text-xs mb-8" style={{ color: 'var(--warm-muted)' }}>
              <li>$1 = 10 credits · top up from $5</li>
              <li>First top-up gets +30% bonus credits</li>
              <li>AI Analyst answers are 1 credit each</li>
            </ul>
            <div className="flex items-center gap-4">
              <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
              <Link to="/pricing" className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: 'var(--warm-muted)' }}>Full pricing <ArrowRight className="size-3.5" /></Link>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ── 15. Trust ────────────────────────────────────────────────────
function TrustSection() {
  const ref = useReveal<HTMLDivElement>();
  const tiles = [
    { icon: IconWorld, title: 'EU data hosting', body: 'Your workspace data lives in Cloudflare\'s European region.' },
    { icon: IconUserShield, title: 'Roles on every request', body: 'Six roles, checked server-side on every API call.' },
    { icon: IconKey, title: 'Encrypted secrets', body: 'Webhook secrets are encrypted at rest; payloads are HMAC-signed.' },
    { icon: IconLink, title: 'Signed file links', body: 'Attachments are served through signed links that expire.' },
    { icon: IconActivityHeartbeat, title: 'Public status page', body: 'Live uptime and response times.' },
    { icon: IconHistory, title: 'Public changelog', body: 'Every release, dated.' },
  ];
  return (
    <Section style={{ background: 'var(--warm-card)', borderTop: '1px solid var(--warm-border)', borderBottom: '1px solid var(--warm-border)' } as React.CSSProperties}>
      <div ref={ref} className="home-reveal">
        <Eyebrow>Trust</Eyebrow>
        <H2>Built carefully, <br /><Muted>run in the open.</Muted></H2>
        <div className="reveal-stagger mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {tiles.map((t) => (
            <div key={t.title} className="p-5 rounded-xl" style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)' }}>
              <div className="mb-4"><IconTile icon={t.icon} bg="var(--warm-card)" /></div>
              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--warm-text)' }}>{t.title}</p>
              <p className="text-sm" style={{ color: 'var(--warm-muted)' }}>{t.body}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ── 16. Coming soon ──────────────────────────────────────────────
function ComingSoonSection() {
  const ref = useReveal<HTMLDivElement>();
  const items = ROADMAP_ITEMS.filter((r) => r.status !== 'shipped')
    .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99))
    .slice(0, 6);
  return (
    <Section>
      <div ref={ref} className="home-reveal">
        <Eyebrow>Roadmap</Eyebrow>
        <H2>What's <br /><Muted>coming next.</Muted></H2>
        <div className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.title} className="p-5 rounded-xl" style={{ border: '2px dashed var(--warm-border)', background: 'transparent' }}>
              <span className="text-xs px-2 py-0.5 rounded-full mb-3 inline-block" style={{ background: 'var(--warm-card)', color: 'var(--warm-muted)', border: '1px solid var(--warm-border)' }}>
                {item.status === 'in-progress' ? 'In development' : 'In the pipeline'}
              </span>
              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--warm-text)' }}>{item.title}</p>
              <p className="text-sm" style={{ color: 'var(--warm-muted)' }}>{item.description}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center gap-4">
          <Link to="/roadmap" className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: 'var(--warm-text)' }}>
            See the full roadmap <ArrowRight className="size-3.5" />
          </Link>
          <span className="text-xs" style={{ color: 'var(--warm-muted)' }}>In priority order. Priorities can change as we learn from customers.</span>
        </div>
      </div>
    </Section>
  );
}

// ── 17. FAQ ──────────────────────────────────────────────────────
const FAQ_ITEMS = [
  { question: 'What is Kora?', answer: 'Kora is an AI helpdesk and issue tracker for support and IT teams. Paste a message and AI turns it into a structured ticket; your team tracks it with SLAs, automation rules, a customer portal and a help center. You pay $0.10 per ticket created, with unlimited members and no monthly plans.' },
  { question: 'How much does Kora cost?', answer: 'Kora is pay as you go: $1 buys 10 credits and each ticket created costs 1 credit, or $0.10. There are no seats and no monthly plans, and members are unlimited. New workspaces get 1,100 free credits with no card. Top-ups start at $5, and your first top-up earns 30% bonus credits.' },
  { question: 'What counts as a ticket?', answer: 'Every ticket created — from the dashboard, the API, an embedded session, a customer form or the customer portal — costs 1 credit. Comments, replies, assigning, resolving, SLA tracking, automation rules and webhooks cost nothing. AI Analyst answers cost 1 credit each, and API usage costs 1 credit per 400 responses.' },
  { question: 'Is there a free plan or trial?', answer: 'There are no plans. Instead, every new workspace gets 1,100 free credits, no card required: 1,000 credits valid for 30 days plus 100 credits valid for 10 years. That covers about 1,100 tickets. After that you top up from $5 and only pay for tickets you create.' },
  { question: 'How fast can we get started?', answer: 'You can be live in minutes. Sign in with Google or an emailed link, name your workspace, pick your ticket categories and claim your free credits. There is no credit card, sales call, setup fee or approval step.' },
  { question: 'What does the AI Analyst do?', answer: 'The AI Analyst answers plain-English questions about your tickets, like "Which categories take longest to resolve?" It writes a safe, read-only query scoped to your workspace, runs it and replies in seconds with tables and the query it used. Each answer costs 1 credit; failed answers are free.' },
  { question: 'What does AI ticket structuring do?', answer: 'Paste any raw text — a Slack thread, a customer email or a quick note — and Kora\'s AI writes a clean ticket title and description, picks the category and sets the priority. You review it and create the ticket. Structuring is free; only the ticket you create costs a credit.' },
  { question: 'Does Kora have a customer portal?', answer: 'Yes. Turn it on and your customers sign in with an emailed link, with no password or account, and see only their own requests. They can reply, reopen, raise new requests and download or delete their data. Browsing and replies are free; a new request costs 1 credit.' },
  { question: 'Do SLAs and automation cost extra?', answer: 'No. SLA tracking and automation rules are free on every workspace. Set response and resolution targets with business hours, pause clocks while you wait on customers, and get breach alerts.' },
  { question: 'Can Kora connect to our other tools?', answer: 'Yes. Kora has a REST API with 20+ endpoints, 10 webhook events signed with HMAC-SHA256, embedded sessions to show Kora inside your own product, and a small form widget for any website. Native Slack and email-to-ticket integrations are on the roadmap.' },
  { question: 'Where is our data stored?', answer: 'Workspace data is hosted in Cloudflare\'s European region, and file attachments are served through signed links that expire. Webhook secrets are encrypted at rest, and permissions for six roles are checked on every request. Customer-portal users can download or delete their own data themselves.' },
  { question: 'Can Kora write our help center for us?', answer: 'Yes. Magic Helpdesk reads your website, plans 4 to 15 help-center articles and writes them as drafts for you to review, edit and publish. A run costs 10 credits and is refunded if it drafts nothing. Each published article then costs 1 credit, and edits are always free.' },
  { question: 'What does Kora not have yet?', answer: 'Email-to-ticket, live chat, a native Slack integration, CSAT surveys and an MCP server for AI assistants are planned and not available yet. Kora AI (reply drafting and a resolution agent) is live. Today you can paste any message and Kora structures it, or connect your tools through the API and webhooks. See the roadmap for what\'s next.' },
];

function FaqSection() {
  const ref = useReveal<HTMLDivElement>();
  const [askQ, setAskQ] = useState('');

  const handleAsk = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!askQ.trim()) return;
    const url = `/ask?q=${encodeURIComponent(askQ)}`;
    window.location.href = url;
  }, [askQ]);

  return (
    <Section style={{ background: 'var(--warm-card)', borderTop: '1px solid var(--warm-border)' } as React.CSSProperties}>
      <div ref={ref} className="home-reveal">
        <Eyebrow>FAQ</Eyebrow>
        <H2>You have questions. <br /><Muted>We have answers.</Muted></H2>

        <div className="mt-8 mb-10 max-w-xl">
          <form onSubmit={handleAsk} className="flex gap-2">
            <input
              value={askQ}
              onChange={(e) => setAskQ(e.target.value)}
              placeholder="Ask anything about Kora…"
              className="flex-1 px-4 py-2.5 text-sm rounded-[10px] outline-none"
              style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}
            />
            <button type="submit" className="warm-btn px-4 py-2.5 text-sm">Ask</button>
          </form>
          <div className="mt-3 flex flex-wrap gap-2">
            {['How is Kora different from Freshdesk?', 'What does a ticket cost?', 'Can customers see their own requests?'].map((q) => (
              <button key={q} onClick={() => setAskQ(q)} className="text-xs px-2.5 py-1 rounded-full transition-colors" style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)', color: 'var(--warm-muted)' }}>
                {q}
              </button>
            ))}
          </div>
          <p className="mt-3 text-sm" style={{ color: 'var(--warm-muted)' }}>
            Prefer to talk? <a href={bookingUrl('faq')} target="_blank" rel="noopener" className="underline">Book a call →</a>
          </p>
        </div>

        <FaqAccordion items={FAQ_ITEMS} twoCol />
      </div>
    </Section>
  );
}

// ── Schema JSON-LD ───────────────────────────────────────────────
function buildHomepageSchema() {
  return [
    {
      '@context': 'https://schema.org',
      '@graph': [
    {
      '@type': 'Organization',
      name: 'Kora',
      url: 'https://usekora.app',
      logo: 'https://usekora.app/assets/logo_square.svg',
      description: 'Kora is a pay-per-ticket AI helpdesk and issue tracker: no seats, no monthly plans, $0.10 per ticket.',
      contactPoint: { '@type': 'ContactPoint', email: 'founder@usekora.app', contactType: 'customer support' },
    },
    {
      '@type': 'WebSite',
      name: 'Kora',
      url: 'https://usekora.app',
      potentialAction: { '@type': 'SearchAction', target: { '@type': 'EntryPoint', urlTemplate: 'https://usekora.app/search?q={search_term_string}' }, 'query-input': 'required name=search_term_string' },
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Kora',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: 'Kora is an AI helpdesk and issue tracker priced per ticket: $0.10 per ticket, unlimited members, no monthly plans. AI ticket structuring, SLA tracking, automation rules and webhooks are free.',
      url: 'https://usekora.app',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Start free: 1,100 free credits on signup, no card required, unlimited members',
      },
    },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: typeof f.answer === 'string' ? f.answer : f.question },
      })),
    },
  ];
}

// ── Main export ──────────────────────────────────────────────────
export function LandingPage() {
  useEffect(() => {
    const title = 'Kora — AI Helpdesk & Issue Tracker, Pay Per Ticket';
    const description = 'AI helpdesk priced per ticket: $0.10 per ticket, unlimited members, no monthly plans. SLA tracking, automation and customer portal included. 1,100 free credits.';
    setDocumentMeta(title, description);
    setCanonical('/');
    setOgImage('/assets/og-home.png');
    // index.html ships a generic Organization block for every page; the homepage carries its own
    // Organization inside the @graph, so drop the static one to avoid a duplicate.
    document.head.querySelectorAll('script[type="application/ld+json"]:not([data-seo-jsonld])').forEach((el) => {
      if (/"@type"\s*:\s*"Organization"/.test(el.textContent || '')) el.remove();
    });
    const cleanup = injectJsonLdList(buildHomepageSchema());
    return cleanup;
  }, []);

  return (
    <MarketingShell>
        <HeroSection />
        <ProofStrip />
        <LiveInMinutes />
        <SeatsVsTickets />
        <CapabilitySection />
        <AIAnalystSection />
        <TeamSection />
        <DeveloperSection />
        <WhoSection />
        <CompareSection />
        <PricingSection />
        <TrustSection />
        <ComingSoonSection />
        <FaqSection />
        <CtaBand />
    </MarketingShell>
  );
}
